import { LitElement, html } from 'lit';
import { property } from 'lit/decorators.js'
import { getAdBlockDetector } from '../../../shared/utilities/utils';

import { Observable, Subject } from 'rxjs';
import { takeUntil, filter } from 'rxjs/operators';
import { getStringOperatingSystem, isInIframe, raiPlayerEvents, utils } from './utils'
import { MediaInfo, PageInfo } from './models'

const unsubscribe$ = new Subject<void>();
let watcherPlayer = false;

const DEBUG_STR = 'RAI_TR';
const trackEnqueue = window.Rai!._trackEnqueue;

type UnwrappedObservable<T> = T extends Observable<infer V> ? V : never;

export class RailyticsWC extends LitElement {

  declare private wt: Rai["RailyticsInstance"];

  constructor() {
    super();
    this.pageInfo = undefined;
    this.mode = undefined;
    this.pageInfoBaseData = undefined;
    this.wt = window.Rai!.RailyticsInstance;
  }

  @property({ type: Object })
  declare pageInfo: PageInfo | undefined

  @property({ type: Object })
  declare mediaInfo: MediaInfo | undefined

  @property()
  declare mode: string | undefined

  @property({ type: Object })
  declare pageInfoBaseData: Record<string, string> | undefined

  private mediaLibraryLoaded = false
  private adBlock = false
  private mediaParams: Record<number, string> | undefined
  private handledEvents: string[] = []

  override updated() {
    !!this.mediaInfo && !this.mediaLibraryLoaded && this.initMediaTrack();
  }

  override render() {
    return html``;
  }

  override firstUpdated() {
    this.adBlock = getAdBlockDetector().detect()
    trackEnqueue.pageQueue
      .pipe(filter(Boolean), takeUntil(unsubscribe$))
      .subscribe({
        next: (pageInfo: any) => {
          if ('mode' in pageInfo) {
            const { linkId, customClickParameter } = pageInfo
            /* tracciamento ACTION sottoscritto per tempistiche su coda di pagina */
            this.trackAction({
              linkId, /* ct */
              customClickParameter: { ...customClickParameter, length: Object.keys(customClickParameter).map(k => parseInt(k)).sort((a, b) => b - a)[0] + 1 }  /* ck[1,2,3] */,
            });
          } else {
            /* tracciamento PAGINA */
            if (this.mode == 'extendInfoByComponent') {
              pageInfo = { ...this.pageInfoBaseData, ...pageInfo };
            }
            this.pageInfo = pageInfo;
            this.trackPage();
          }
        },
        error: (e: any) => {
          console.warn(e);
        },
      });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    unsubscribe$.next();
    unsubscribe$.complete();
  }

  private getMediaInfo(liveOverride: Partial<MediaInfo>) {
    const mediaInfo = liveOverride ?? this.mediaInfo; /* se presenti utilizzo info provenienti da onAir, esposte dal player */
    const track_info = mediaInfo.track_info!

    const isArray = (el: unknown): el is string[] => {
      return !!el && Array.isArray(el) && !!el.length
    }

    const mg = [
      document.referrer && document.referrer.indexOf('msn.com') > -1 ? 'msn' : 'web' /*--mg1--*/,
      track_info['channel'] /*--mg2--*/,
      track_info['editor'] /*--mg3--*/,
      track_info['program_title'] /*--mg4--*/,
      track_info['media_type'] /*--mg5--*/,
      track_info['form'] /*--mg6--*/,
      track_info['episode_title'] ?? track_info['title'] /*--mg7--*/,
      track_info['season'] ?? track_info['edit_year'] ?? track_info['year'] /*--mg8--*/,
      track_info['episode_number'] /*--mg9--*/,
      isArray(track_info['genres']) ?
        track_info['genres'][0] :
        isArray(track_info['program_genres']) ?
        track_info['program_genres'][0] :
        null /*--mg10--*/,
      isArray(track_info['sub_genres'])
        ? track_info['sub_genres'][0]
        : isArray(track_info['program_sub_genres'])
        ? track_info['program_sub_genres'][0]
        : null /*--mg11--*/,
      track_info['typology'] /*--mg12--*/,
      track_info['domain'] /*--mg13--*/,
    ].reduce((acc: string, currVal, index, mg) => {
      if (currVal) {
        return `${acc}mg${index + 1}=${currVal}${index + 1 === mg.length ? '' : ';'}`
      } else return acc;
    }, '');

    const mi = [
      !!document.referrer && document.referrer.indexOf('msn') > -1
        ? 'msn'
        : 'web',
      track_info['domain'],
      track_info['media_type'],
      track_info['program_title'],
      track_info['episode_title'] || track_info['title'],
      track_info['id'],
    ].filter(Boolean)
    .join(' - ');

    this.mediaParams = [
      mi /* mi */,
      'playerEvent' /* mk*/,
      'currentEventTime' /* mt1 */,
      'mediaDuration' /* mt2 */,
      mg /* mg[1/13] */,
      null /* bw = bandwidth */,
      'event.volume' /* vol */,
      'event.muted' /* mut */,
    ].reduce((obj, val, idx) => {
      if (val) {
        obj[idx] = val
      }
      return obj
    }, {} as Record<number, string>);
    return mediaInfo;
  }

  private initMediaTrack() {
    this.mediaLibraryLoaded = true;
    // this.wt.sendinfoMedia = window.wt_sendinfo_media;

    const events = raiPlayerEvents.media;
    this.handledEvents = [
      events.READY,
      events.FIRSTPLAY,
      events.PLAY,
      events.ADS.STARTED,
      events.PAUSE,
      events.SEEK_END,
      events.ADS.SKIPPED,
      events.HEARTBEAT,
      events.ENDED,
      events.DISPOSE,
      events.PROGRAM_INFO_UPDATED,

      events.AUDIO_CHANGED,
      events.SUBTITLE_CHANGED,
      events.PIP_START,
      events.PIP_STOP,
      events.FULLSCREEN_EVENT,

      events.DRM_LICENSE_STATUS_INTERNAL_ERROR,
      events.ERROR,
      events.MEDIAPOLIS_PARSE_ERROR,
      events.NOT_SUPPORTED_MEDIA_TYPE,
      events.ADS.ADSERROR,
    ];

    trackEnqueue.mediaQueue /* media track */
      .pipe(
        filter((plEvent: any) => this.handledEvents.includes(plEvent.type)),
        takeUntil(unsubscribe$)
      )
      .subscribe((plEvent: any) => {
        try {
          this.trackMedia(plEvent.detail);
        } catch (e) {
          console.error(e);
        }
      });
  }

  trackPage() {
    if (
      this.pageInfo &&
      this.pageInfo['domain'] === 'rainews' &&
      this.pageInfo['content'] === 'homepage' &&
      'traffic_origin' in this.pageInfo && this.pageInfo['traffic_origin'] !== 'app'
    ) {
      if (!document.hasFocus()) { this.pageInfo['channel'] = 'nofocus'; }
    }
    const pageInfo = this.pageInfo!;
    const wt = this.wt;
    const getCustomParam = () => {
      let customParam = getStringOperatingSystem() + ';';
      customParam += `adBlock:${this.adBlock ? 'true' : 'false'}${(this.pageInfo && 'customParam' in this.pageInfo ? `;${this.pageInfo['customParam'] as string}` : '')}`;
      if (isInIframe()) {
        const iframeUrl = typeof document.referrer == 'string' ? document.referrer : 'unknown';
        customParam += ';iframe=' + iframeUrl;
      }
      const predictHpType = 'predictHpType' in window ? window['predictHpType'] : ''
      if (typeof predictHpType === 'string' && predictHpType) {
        customParam += ';predictHpType=' + predictHpType;
      } else {
        try {
          const predictHpRef = utils.cookie.get('predictPageRef');
          if (predictHpRef) {
            customParam += ';predictHpType=' + predictHpRef;
          }
          utils.cookie.remove('predictPageRef', { path: '/', domain: document.location.host });
        } catch (e) {
          // pass
        }
      }
      return customParam;
    };
    const cleanGroup = function (arr: unknown[]) {
      for (let i = 0; i < arr.length; i++) {
        if (!arr[i]) {
          delete arr[i]
        } else {
          const val = arr[i]
          if (typeof val === 'string') {
            arr[i] = val.toLowerCase().replace(/['`’]/g, '')
          } else {
            arr[i] = (val as string).toString()
          }
        }
      }
      return arr as string[];
    };

    const contentGroup = [
      null,
      'aggregato' /* cg1 */,
      pageInfo['domain'] /* cg2 */,
      pageInfo['typology'] || pageInfo['program_typology'] /* cg3 */,
      pageInfo['program_title'] /* cg4 */,
      pageInfo['content'] /* cg5 */,
      null /* cg6 */,
      null /* cg7 */,
      pageInfo['program_title'] /* cg8 */,
      pageInfo['section'] /* cg9 */,
      pageInfo['sub_section'] /* cg10 */,
      !!pageInfo['genres'] && pageInfo['genres'].length
        ? pageInfo['genres'][0]
        : pageInfo['program_genres']
        ? pageInfo['program_genres'][0]
        : null /* cg11 */,
      !!pageInfo['sub_genres'] && pageInfo['sub_genres'].length
        ? pageInfo['sub_genres'][0]
        : pageInfo['program_sub_genres']
        ? pageInfo['program_sub_genres'][0]
        : null /* cg12 */,
      pageInfo['year'] /* cg13*/,
      pageInfo['edition'] /* cg14 */,
    ];
    const customParameter = [
      null,
      /[\\?&]refresh_ce/.test(window.location.search)
        ? 'refresh_ce'
        : 'no-refresh' /* cp1 */,
      pageInfo['channel'] /* cp2 */,
      pageInfo['date'] /* cp3 */,
      pageInfo['page_type'] /* cp4 */,
      !!pageInfo['genres'] && pageInfo['genres'].length /* cp5 */
        ? pageInfo['genres'].toString().replace(/,/g, ';') +
          (!!pageInfo['sub_genres'] && pageInfo['sub_genres'].length
            ? ';' + pageInfo['sub_genres'].toString().replace(/,/g, ';')
            : '') +
          ';' + getCustomParam()
        : !!pageInfo['program_genres'] && pageInfo['program_genres'].length
        ? pageInfo['program_genres'].toString().replace(/,/g, ';') +
          (!!pageInfo['program_sub_genres'] && pageInfo['program_sub_genres'].length
            ? ';' + pageInfo['program_sub_genres'].toString().replace(/,/g, ';')
            : '') +
          ';' + getCustomParam()
        : getCustomParam(),
      pageInfo['season'] /* cp6 */,
      pageInfo['episode_title'] /* cp7 */,
      pageInfo['episode_number'] /* cp8 */,
      window.startLoadDate ? Date.now() - window.startLoadDate : null /* cp9 */,
      ('traffic_origin' in pageInfo ? pageInfo['traffic_origin'] : 'browsing') /* cp10 */,
      ('device' in pageInfo ? pageInfo['device'] : 'browser') /* cp11 */,
      pageInfo['platform'] !== '[platform]' ? pageInfo['platform'] : 'web' /* cp12 */,
      pageInfo['editor'] /* cp13 */,
      pageInfo['typology'] /* cp14 */,
    ];

    const contentGroup_ = cleanGroup(contentGroup);
    const customParameter_ = cleanGroup(customParameter);
    const contentId = document.location.protocol + '//' + document.location.host + document.location.pathname;
    const internalSearch = pageInfo && 'cerca_per' in pageInfo ? pageInfo['cerca_per'] as string : '';
    const url = new URL(document.location.href);
    const searchParams = url.searchParams;
    const campaign = searchParams.getAll('wt_mc').map(cmp => encodeURIComponent(`wt_mc=${cmp}`)).join(';');
    const params: ParamsMappingData = {
      contentGroup: contentGroup_,
      customParameter: customParameter_,
      internalSearch,
      campaignId: !!campaign ? campaign : undefined
    }

    const Raisso = window.Rai?.Raisso
    const ProfileManager = window.Rai?.ProfileManager
    const raiSsoUserObject = ProfileManager?.getUserProfile()

    if (Raisso?.checkLogin() && !!raiSsoUserObject) {
      /*dati utente*/
      if (
        raiSsoUserObject['birthYear'] &&
        typeof raiSsoUserObject['age'] === 'number' &&
        raiSsoUserObject['age'] >= 18
      ) {
        params.birthday = raiSsoUserObject['birthYear'] + '-01-01' /* uc707 */
      }
      params.customerId = raiSsoUserObject['ua'] ?? undefined /* cd */
      params.gender = raiSsoUserObject['gender'] ?
        raiSsoUserObject['gender'].toLocaleLowerCase() === 'm' ? '1' : '2'
        : '3' /* uc706 */
      params.urmCategory = []
      params.urmCategory[1] = raiSsoUserObject['personalizzazione'] ? 'true' : 'false' /* uc1 */
    }

    try {
      wt?.sendInfo(params, contentId, 'page');
    } catch (err) {
      // pass
    }
  }

  trackAction(actionInfo: { linkId: string, customClickParameter: ArrayLike<string> }) {
    const wt = this.wt;
    const contentId = document.location.protocol + '//' + document.location.host + document.location.pathname;
    wt?.sendInfo({
      customClickParameter: Array.prototype.slice.call(actionInfo.customClickParameter),
      linkId: actionInfo.linkId
    }, contentId, 'click');
  }

  trackMedia(event: UnwrappedObservable<typeof trackEnqueue.mediaQueue>['detail']) {
    if (!event['eventData']) throw 'Missing player event data';
    const eventInfo = event['eventData'];
    const events = raiPlayerEvents.media;
    const sendinfo_media = this.wt!.sendInfoMedia.bind(this.wt);

    const paramsPrev = this.mediaParams ? {...this.mediaParams} : {}; //dal secondo evento immagazzinerà le mediaInfo dell'evento precedente.

    if (
      eventInfo['eventName'] === events.PROGRAM_INFO_UPDATED || eventInfo['eventName'] === events.READY
    ) {
      this.getMediaInfo(
        event['mediaInfo']['program_info']
      ); /*aggiorno mediaInfo solo su cambio di media*/
    }

    const track_info = this.mediaInfo?.['track_info'];
    const params = {...this.mediaParams}

    paramsPrev[2] = Math.round('currentTime' in eventInfo ? eventInfo['currentTime'] as number : 0).toString();
    paramsPrev[3] = Math.round(eventInfo['mediaDuration']).toString();
    paramsPrev[6] = 'volume' in eventInfo ? eventInfo['volume'] as string : '';
    paramsPrev[7] = 'muted' in eventInfo ? eventInfo['muted'] as string : '';

    params[2] = Math.round('currentTime' in eventInfo ? eventInfo['currentTime'] as number : 0).toString();
    params[3] = Math.round(eventInfo['mediaDuration']).toString();
    params[6] = 'volume' in eventInfo ? eventInfo['volume'] as string : '';
    params[7] = 'muted' in eventInfo ? eventInfo['muted'] as string : '';

    switch (eventInfo['eventName']) {
      case events.READY:
        params[1] = 'init';
        sendinfo_media(params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]);

        params[1] = track_info?.['media_type'] ?? '';
        sendinfo_media(params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]);

        params[1] = 'web';
        break;
      case events.FIRSTPLAY:
        params[1] = 'start content';
        sendinfo_media(params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]);

        params[1] = 'play';

        if (!watcherPlayer) {
          sendinfo_media(
            params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]
          ); /**invio "play". altrimenti sovrascritto da play300 */
          watcherPlayer = true;
          const interv = setInterval(function () {
            params[1] = 'play300';
            sendinfo_media(params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]);
            clearInterval(interv);
          }, 300);
        }

        break;
      case events.PLAY:
        params[1] = 'play';
        break;
      case events.ADS.STARTED:
        params[1] = `spot${eventInfo['mediaType']} ${'sequenceIndex' in eventInfo ? eventInfo['sequenceIndex'] as number : 0}`;
        break;
      case events.PAUSE:
        params[1] = 'pause';
        break;
      case events.SEEK_END:
        params[1] = 'seek';
        break;
      case events.ADS.SKIPPED:
        params[1] = 'vastSkipped';
        break;
      case events.HEARTBEAT:
        if (track_info?.['media_type'] === 'live') {
          params[1] = 'uptime';
          sendinfo_media(params[0], params[1], params[2], params[3], params[4], params[5], params[6], params[7]);
        }
        params[1] = 'pos';
        break;
      case events.ENDED:
        params[1] = 'eof';
        break;
      case events.DISPOSE:
        params[1] = 'stop';
        break;
      case events.PROGRAM_INFO_UPDATED:
        paramsPrev[1] = 'eof';
        sendinfo_media(params[0], params[1] , params[2], params[3], params[4] , params[5] , params[6], params[7]);

        params[1] = 'init';
        sendinfo_media(params[0] , params[1], params[2], params[3], params[4] , params[5] , params[6], params[7]);

        params[1] = track_info?.['media_type'] ?? '';
        sendinfo_media(params[0] , params[1], params[2], params[3], params[4] , params[5] , params[6], params[7]);

        params[1] = 'web';
        sendinfo_media(params[0] , params[1], params[2], params[3], params[4] , params[5] , params[6], params[7]);

        params[1] = 'start content';
        sendinfo_media(params[0] , params[1], params[2], params[3], params[4] , params[5] , params[6], params[7]);

        params[1] = 'play';
        break;
      case events.AUDIO_CHANGED:
        params[1] =
          !!eventInfo['additionalData'] && !!eventInfo['additionalData']['audioTrack']
            ? eventInfo.additionalData['audioTrack']
            : 'audio changed';
        break;
      case events.SUBTITLE_CHANGED:
        params[1] =
          !!eventInfo.additionalData && !!eventInfo.additionalData['subtitleTrack']
            ? eventInfo.additionalData['subtitleTrack']
            : 'subtitle changed';
        break;
      case events.PIP_START:
      case events.PIP_STOP:
        params[1] =
          !!eventInfo.additionalData && !!eventInfo.additionalData['pipMode']
            ? eventInfo.additionalData['pipMode']
            : 'PIP changed';
        break;
      case events.FULLSCREEN_EVENT:
        params[1] = 'fullscreen' in eventInfo && eventInfo.fullscreen ? 'fullscreen' : 'windowed';
        break;

      case events.DRM_LICENSE_STATUS_INTERNAL_ERROR:
      case events.ERROR:
      case events.MEDIAPOLIS_PARSE_ERROR:
      case events.NOT_SUPPORTED_MEDIA_TYPE:
      case events.ADS.ADSERROR:
        params[1] = 'ERROR: ' + eventInfo.eventName;
        watcherPlayer = false;
        break;
    }
    if (watcherPlayer) {
      watcherPlayer = false;
    } else {
      if (eventInfo.mediaType) {
        sendinfo_media(params[0] , params[1] , params[2], params[3], params[4] , params[5] , params[6], params[7]);
      }
    }
  }

  traceDebug(...params: (string | number)[]) {
    console.log(DEBUG_STR, params);
  }
}
